import type { PanelType } from "../outreach/panel/types";
import { substituteConceptPlaceholders } from "./conceptPlaceholders";
import {
  DEFAULT_CONCEPT_SAMPLING_FUNCTION_ID,
  safeValidateDynamicSamplingParams,
  sortConceptsForSampling,
} from "./conceptSampling";
import type { AllConceptSamplingData } from "./conceptSampling/functions";
import { buildConjointDesign } from "./conjoint";
import { formatConjointAnswer } from "./conjoint/answerFormat";
import { buildGaborGrangerDesign } from "./gaborGranger";
import { formatGaborGrangerAnswer } from "./gaborGranger/answerFormat";
import { buildMaxDiffDesign } from "./maxdiff";
import { formatMaxDiffAnswer } from "./maxdiff/answerFormat";
import type {
  AState,
  Concept,
  ConceptId,
  ConditionalDef,
  ConditionalDefSingle,
  ConjointDesign,
  EmbedDef,
  GaborGrangerDesign,
  InterviewBlock,
  InterviewBlockId,
  InterviewConceptBlock,
  InterviewItem,
  InterviewItemId,
  InterviewItemTemplate,
  InterviewItemTemplateId,
  InterviewSampleState,
  MaxDiffDesign,
  MediaDef,
  MediaDefOrPlaceholder,
  Option,
  OptionId,
  OptionMedia,
  QAState,
  QState,
  ResponseInterviewFileRef,
  ScreenerData,
} from "./types";
import { getMediaDefs } from "./utils";
import {
  OptionsResolverUtils,
  getOptionsUnsafe,
} from "./utils/optionsResolverUtils";
import * as Prolific from "@/components/outreach/panel/prolific/constants";
import {
  buildAnswerValuesForItems,
  substituteAnswerReferences,
  type AnswerReferenceDecorator,
  type AnswerValues,
} from "@/components/study-data/answerReferenceUtils";
import { formatMatrixSelections } from "@/lib/interview/matrix/formatMatrixAnswer";
import { assertNever } from "@/packages/ts-utils";
import { get_config } from "@/utils/config";
import { notNull, shuffleArray } from "@/utils/helpers";
import { logger } from "@/utils/logging/isomorphic";
import { redactTokenPathSegments } from "@/utils/logging/redact";
import { areaTag, variantKindTag } from "@/utils/o11y/tags";
import {
  combineConditionals,
  filterInterviewParts,
  isConjoint,
  getActiveAddInstructions,
  getBlockConditional,
  isGaborGranger,
  isMaxDiff,
  isMatrix,
  isMultipleChoice,
  isRanking,
  passesConditional,
  questionWeight,
} from "@/utils/questions";
import type {
  AdditionalResponseData,
  AudioLanguage,
  ChatGPTMessage,
  FreeResponse,
  SampledSurveyData,
  SampledSurveyDataSingleLanguage,
  ScreenerStatus,
  SurveyData,
  SurveyDataSingleLanguage,
  SurveyRevision,
} from "@/utils/types";
import seedrandom from "seedrandom";
import { v4 as uuidv4, v5 as uuidv5 } from "uuid";

export const createEmptyQState = (firstItem: InterviewItem): QState => ({
  assMsg: "",
  currentItemId: firstItem.id,
  currentTemplateId: firstItem.templateId,
  currentConceptId: firstItem.conceptId,
  edited: false,
});

export const emptyAState: AState = {
  inputType: "text" as const,
  input: "",
  voiceInput: "",
  rawInput: "",
  selectedOptionIds: [],
  language: "en",
};
export const createQAStateFromItem = (
  item: InterviewItem,
  {
    language,
    useQuestionText = true,
    // those are usually search params or additional data. replacing {{key}} with the value
    placeholderValues = {} as Record<string, string>,
    answerValues = {} as AnswerValues,
  }: {
    language: AudioLanguage;
    useQuestionText?: boolean;
    placeholderValues?: Record<string, string>;
    answerValues?: AnswerValues;
  }
): QAState => {
  const qText = useQuestionText
    ? format_message(item.text, placeholderValues, {
        removeTitle: true,
        answerReferences: { answerValues, language },
      })
    : "";
  return {
    ...createEmptyQState(item),
    assMsg: qText,
    ...emptyAState,
    language,
  };
};

/** Most recent uploaded file for the current item (follow-up turns omit fileUpload). */
export const getFileRefForCurrentItem = (
  history: QAState[],
  currentItemId: InterviewItemId | null
): ResponseInterviewFileRef | undefined => {
  if (!currentItemId) return undefined;
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry.currentItemId !== currentItemId) continue;
    const fileRef = entry.fileUpload?.fileRef;
    if (fileRef) return fileRef;
  }
  return undefined;
};

// Legacy responses did not have history but we do not care about it any more
export const getHistory = (response: FreeResponse) => response.history;

export const isHistoryNotEmpty = (
  history?: QAState[],
  status?: ScreenerStatus | null
): history is QAState[] => {
  if (!history || history.length == 0) return false;
  // if we have a welcome message and the user only answered that we count it
  // as not started.
  // We don't know if the linked item is type statement or not.
  // So, our rule is that you need to answer one open end or 2 multiple choice
  // Note that this is also off if we have a one-item screener but no other questions answered.
  // But in some cases we don't have the screener data available so this is our best guess.
  const openEndAnswers = history.filter(
    (s) => (s.rawInput ?? "").trim().length > 0 && s.inputType !== "button"
  );
  const allAnswers = history.filter((s) => {
    const hasRawInput = (s.rawInput ?? "").trim().length > 0;
    const hasSubmittedSelection =
      s.submittedAt !== undefined &&
      ((s.selectedOptionIds?.length ?? 0) > 0 ||
        s.maxDiffScreen?.bestPick !== undefined ||
        s.maxDiffScreen?.worstPick !== undefined);
    return hasRawInput || hasSubmittedSelection;
  });
  // TODO(future):We might want to update this if we end up adding a welcome message before screening
  const isScreening = status !== "qualified";
  // if we are screening or screened out and they have answered any questions at all we want to use the response
  return openEndAnswers.length > 0 || allAnswers.length > (isScreening ? 0 : 1);
};

export type ImportantRevisionData = Pick<SurveyRevision, "data" | "config">;

const getSelectedOptionsForItem = (
  state: QAState,
  relevantInterviewItem: InterviewItem
) => {
  // Matrix selections are composite "{rowId}:{choiceId}" ids that don't
  // appear in `choices`; decode them into per-row strings instead.
  if (isMatrix(relevantInterviewItem)) {
    return formatMatrixSelections({
      rows: relevantInterviewItem.rows ?? [],
      choices: relevantInterviewItem.choices ?? [],
      selectedOptionIds: state.selectedOptionIds,
    });
  }
  const selectedOptions = state.selectedOptionIds
    .map((id) => {
      const choice = relevantInterviewItem.choices?.find((c) => c.id === id);
      return choice?.text;
    })
    .filter(notNull);

  if ((selectedOptions?.length ?? 0) !== state.selectedOptionIds.length) {
    console.error(
      "Selected options length does not match selected option ids length",
      state,
      selectedOptions
    );
  }
  return selectedOptions;
};

export const getSelectedOptions = (
  state: QAState,
  sampledData: SampledSurveyDataSingleLanguage
) => {
  if (!state.selectedOptionIds) return undefined;
  if (state.selectedOptionIds.length === 0) return [];
  const relevantInterviewItem = sampledData.allInterviewItems.find(
    (item) => item.id === state.currentItemId
  );
  if (!relevantInterviewItem) {
    console.error("History item ID not found in sampled data", state);
    return undefined;
  }
  return getSelectedOptionsForItem(state, relevantInterviewItem);
};

/*
returns the user message for a given state
selected options will be in the language of sampledData
*/
export const getUserMessage = (
  state: QAState,
  sampledData: SampledSurveyDataSingleLanguage,
  { force_use_raw_input }: { force_use_raw_input: boolean } = {
    force_use_raw_input: false,
  }
) => {
  const hasSelections =
    !!state.selectedOptionIds && state.selectedOptionIds.length > 0;
  // Resolve the item when there are selections OR a conjoint task: a conjoint
  // "none of these" pick has empty selectedOptionIds but still needs formatting.
  const relevantInterviewItem =
    hasSelections || state.conjointTask
      ? sampledData.allInterviewItems.find(
          (item) => item.id === state.currentItemId
        )
      : undefined;
  if (hasSelections && !relevantInterviewItem) {
    console.error("History item ID not found in sampled data", state);
  }
  if (
    state.maxDiffScreen &&
    relevantInterviewItem &&
    isMaxDiff(relevantInterviewItem)
  ) {
    return formatMaxDiffAnswer({
      maxDiffScreen: state.maxDiffScreen,
      options: relevantInterviewItem.choices ?? [],
      metric: relevantInterviewItem.metric,
    });
  }
  if (
    state.conjointTask &&
    state.conjointTask.chosenProfileIndex !== undefined &&
    relevantInterviewItem &&
    isConjoint(relevantInterviewItem) &&
    relevantInterviewItem.type === "question"
  ) {
    return formatConjointAnswer({
      task: state.conjointTask,
      chosenProfileIndex: state.conjointTask.chosenProfileIndex,
      attributes: relevantInterviewItem.attributes ?? [],
    });
  }
  // A rung records its answer on the rung and selects no option, so without this the
  // message is blank and a following LLM turn loses the price just answered. Looked
  // up separately since `relevantInterviewItem` needs selections.
  if (state.gaborGrangerRung) {
    const gaborGrangerItem = sampledData.allInterviewItems.find(
      (item) => item.id === state.currentItemId
    );
    if (gaborGrangerItem && isGaborGranger(gaborGrangerItem)) {
      return formatGaborGrangerAnswer({
        rung: state.gaborGrangerRung,
        currencyCode: gaborGrangerItem.currencyCode,
      });
    }
  }
  const selectedOptions =
    hasSelections && relevantInterviewItem
      ? getSelectedOptionsForItem(state, relevantInterviewItem)
      : [];
  const joiner =
    relevantInterviewItem?.type === "question" &&
    relevantInterviewItem.questionType === "matrix"
      ? " | "
      : ", ";
  const input = (
    force_use_raw_input
      ? // remove brackets from low-confidence words
        state.rawInput.replaceAll("[", "").replaceAll("]", "")
      : state.input_transl || state.input
  ).trim();
  if (state.inputType === "button") {
    // For button submissions, `input` should be empty unless the respondent selected "other".
    // We only use it if voiceInput is empty since this indicates it was written and not typed.
    const hasVoiceLeak = !!state.voiceInput?.trim();
    if (
      !hasVoiceLeak &&
      input.length > 0 &&
      selectedOptions.join(joiner).trim() !== input.trim()
    )
      return [...selectedOptions, input].join(joiner);
    return selectedOptions.join(joiner);
  } else if (state.inputType === "voice") {
    // `voiceInput` is the raw transcript, which redaction does not rewrite.
    return input || (state.redacted ? "" : state.voiceInput);
  }
  if (selectedOptions?.length > 0)
    return [...selectedOptions, input].join(joiner);
  return input;
};

export const getAssMessage = (state: QAState) => {
  const assMsg = state.assMsg_transl ?? state.assMsg;
  if (state.edited) {
    return "USER UPDATED RESPONSE FOR: " + assMsg;
  }
  return assMsg;
};

export const isVideoQualityCheckItem = (
  templateId: InterviewItemTemplateId,
  data: { screener_data?: ScreenerData }
) => {
  return data.screener_data?.videoQualityCheckItem?.id === templateId;
};

export const isInterviewEndItem = (
  templateId: InterviewItemTemplateId,
  data: { blocks: InterviewBlock[] }
) => {
  return data.blocks
    .flatMap((block) => block.items)
    .some((item) => item.id === templateId && item.type === "interview_end");
};

export const getScreenOutPanelPreamble = (panel: PanelType | null) => {
  switch (panel) {
    case "prolific":
      return Prolific.additional_screen_out_text + "\n\n";
    case "user_interviews":
    case "respondent_b2c":
    case "respondent_b2b":
    case "custom":
    case "panel_api":
    case null:
      return "";
    default:
      assertNever(panel);
  }
};

export const deleteLastAssMsg = (rawMsgs: ChatGPTMessage[]) => {
  // deletes last message but only if it an assistant message
  if (rawMsgs.length === 0) {
    return rawMsgs;
  }
  const lastMsg = rawMsgs[rawMsgs.length - 1];
  if (lastMsg.role === "assistant") {
    return rawMsgs.slice(0, rawMsgs.length - 1);
  }
  return rawMsgs;
};

export const format_message = (
  msg: string,
  placeholderValues?: { [key: string]: string | string[] | undefined },
  {
    removeTitle = false,
    answerReferences,
  }: {
    removeTitle?: boolean;
    answerReferences?: {
      answerValues: AnswerValues;
      language: AudioLanguage;
      decorate?: AnswerReferenceDecorator;
    };
  } = {}
) => {
  let m = msg;

  // Substitute answer references using the shared utility (with proper UUID validation)
  if (answerReferences) {
    m = substituteAnswerReferences({ text: m, ...answerReferences });
  }

  if (placeholderValues) {
    for (const key in placeholderValues) {
      const value = placeholderValues[key];
      m = m.split(`{{${key}}}`).join(value?.toString() || "");
    }
  }
  if (removeTitle) {
    m = m.replace(/^\[[^\]]+\]\s*/, "");
  }
  return m;
};

export const buildAnswerValues = (
  history: QAState[],
  sampledData: SampledSurveyDataSingleLanguage
): AnswerValues =>
  buildAnswerValuesForItems(history, sampledData.allInterviewItems);

/** Resolves {{answer:...}} tokens in item text before the discussion guide is sent to /api/chat. */
export const substituteAnswerReferencesInItems = ({
  items,
  answerValues,
  language,
}: {
  items: InterviewItem[];
  answerValues: AnswerValues;
  language: AudioLanguage;
}): InterviewItem[] =>
  items.map((item) =>
    typeof item.text === "string"
      ? {
          ...item,
          text: substituteAnswerReferences({
            text: item.text,
            answerValues,
            language,
          }),
        }
      : item
  );

export class SamplingError extends Error {}

export const flattenConditional = (
  conditional: ConditionalDef
): ConditionalDefSingle[] => {
  if (!conditional) return [];
  if (
    conditional.type === "conjunction" ||
    conditional.type === "disjunction"
  ) {
    return conditional.items.flatMap(flattenConditional);
  }
  return [conditional];
};

/**
 * Computes whether each item's conditional can be checked by processing items in dependency order.
 * A conditional can be checked if all of the inputs are either submitted OR if we know they will never appear.
 *
 * Uses dynamic programming to efficiently resolve transitive dependencies between questions. For example,
 * if q3 depends on q2 which depends on q1, we need to determine if q2's conditional can be checked before
 * we can determine if q3's conditional can be checked.
 *
 * Edge cases and complications handled:
 * - Transitive dependencies: Questions that depend on other conditional questions, creating dependency chains
 * - Skipped questions: Questions filtered out by their conditionals that will never appear
 * - Complex conditionals: Conjunction/disjunction operators with multiple dependencies that need recursive checking
 * - LLM skipping: When the LLM skips over a question that hasn't been submitted, we detect this by checking
 *   if currTemplateId is past the input item's index, allowing us to know the input will never appear
 * - Circular dependencies: Gracefully handles circular references by checking filteredInterviewItems
 * - Missing items: Safely handles references to non-existent questions (logs error and continues)
 * - Performance: O(n) complexity via memoization for chains with up to hundreds of interdependent questions
 */
export const isConditionalWaitingForInput = ({
  conditional,
  qaHistory,
  interviewItems,
  filteredInterviewItems,
  currTemplateId,
  additionalData,
}: {
  conditional: ConditionalDef;
  qaHistory: QAState[];
  interviewItems: InterviewItem[];
  filteredInterviewItems: InterviewItem[];
  currTemplateId?: InterviewItemTemplateId;
  additionalData?: AdditionalResponseData | undefined;
}): boolean => {
  const submittedAnswers =
    qaHistory?.filter((s) => notNull(s.submittedAt)) ?? [];

  // Build a map of templateId -> InterviewItem for O(1) lookup
  const itemMap = new Map<string, InterviewItem>();
  interviewItems.forEach((item) => {
    itemMap.set(item.templateId, item);
  });

  const itemToIndex = new Map<InterviewItemTemplateId, number>();
  interviewItems.forEach((item, index) => {
    itemToIndex.set(item.templateId, index);
  });

  // Build a set of filtered items for O(1) lookup
  const filteredSet = new Set(
    filteredInterviewItems.map((item) => item.templateId)
  );

  // Build a set of submitted templateIds for O(1) lookup
  const submittedSet = new Set(
    submittedAnswers.map((s) => s.currentTemplateId)
  );

  // DP memo: templateId -> is waiting for input
  const isWaitingForInput = new Map<InterviewItemTemplateId, boolean>();

  // Helper to check if a single flat conditional (selectedTemplateOptions) can be checked
  const isSingleConditionalWaitingForInput = (
    cond: ConditionalDef
  ): boolean => {
    if (cond.type === "segment") {
      if (additionalData?.matchingSegmentIds == null) {
        return true;
      }
      return false;
    }

    if (cond.type !== "selectedTemplateOptions") {
      return false;
    }

    // If the input is submitted, we can check this conditional
    if (submittedSet.has(cond.templateId)) {
      return false;
    }

    const inputItem = itemMap.get(cond.templateId);

    // Item doesn't exist, assume we can check
    if (!inputItem) {
      console.error(
        `Input item ${cond.templateId} not found in interview items`
      );
      return false;
    }

    const inputItemIndex = itemToIndex.get(inputItem.templateId) ?? -1;
    const currItemIndex = currTemplateId
      ? (itemToIndex.get(currTemplateId) ?? -1)
      : -1;

    // We're at a later item than the input so its safe to assume the input will never appear
    if (inputItemIndex < currItemIndex) {
      return false;
    }

    // No conditional on input item, need to wait for submission
    if (!inputItem.conditional) {
      return true;
    }

    // If the input itself is waiting for input, we need to wait for it
    const isInputWaitingForInput = isWaitingForInput.get(inputItem.templateId);
    if (isInputWaitingForInput === true) {
      return true;
    }

    // If the input item will appear (is in filtered), we need to wait for it
    if (filteredSet.has(inputItem.templateId)) {
      return true;
    }

    // Input item will never appear, so we don't need to wait
    return false;
  };

  interviewItems.forEach((item) => {
    if (!item.conditional) {
      isWaitingForInput.set(item.templateId, false);
      return;
    }
    const flatConditionals = flattenConditional(item.conditional);
    const result = flatConditionals.some(isSingleConditionalWaitingForInput);
    isWaitingForInput.set(item.templateId, result);
  });

  // Now check the target conditional
  const flatConditionals = flattenConditional(conditional);
  return flatConditionals.some(isSingleConditionalWaitingForInput);
};

const canBlockBeSampled = ({
  blockConditional,
  blockConcepts,
  qaHistory,
  onlyTakeConcept,
  includeAllConcepts,
  prevBlocksInterviewItems,
  filteredPrevBlocksInterviewItems,
  currTemplateId,
  screeningBlockId,
  additionalData,
}: {
  blockConditional: ConditionalDef | undefined;
  blockConcepts: Concept[];
  qaHistory: QAState[] | undefined;
  onlyTakeConcept: "first" | ConceptId | null;
  includeAllConcepts: boolean;
  prevBlocksInterviewItems: InterviewItem[];
  filteredPrevBlocksInterviewItems: InterviewItem[];
  currTemplateId: InterviewItemTemplateId | undefined;
  screeningBlockId: InterviewBlockId | undefined;
  additionalData: AdditionalResponseData | undefined;
}) => {
  if (onlyTakeConcept !== null || includeAllConcepts) {
    return true;
  }

  // defer sampling until screening is complete to ensure segments are
  // available for sampling functions that require them
  const answeredTemplates = new Set(
    qaHistory
      ?.filter((qa) => notNull(qa.submittedAt))
      .map((s) => s.currentTemplateId)
  );
  const filteredScreeningItems = screeningBlockId
    ? filteredPrevBlocksInterviewItems.filter(
        (item) =>
          item.blockId === screeningBlockId &&
          // for some reason this can be part of the screening block idk why
          item.type !== "video_quality_check"
      )
    : [];
  const isScreeningComplete = screeningBlockId
    ? filteredScreeningItems.every((item) =>
        answeredTemplates.has(item.templateId)
      )
    : true;

  if (!isScreeningComplete) {
    return false;
  }

  const flatConditionals = [
    ...(blockConditional ? flattenConditional(blockConditional) : []),
    ...blockConcepts.flatMap((c) =>
      c.conditional ? flattenConditional(c.conditional) : []
    ),
  ];

  return flatConditionals.every((conditional) => {
    return !isConditionalWaitingForInput({
      conditional,
      qaHistory: qaHistory ?? [],
      interviewItems: prevBlocksInterviewItems,
      filteredInterviewItems: filteredPrevBlocksInterviewItems,
      currTemplateId,
      additionalData,
    });
  });
};

const getUrlConceptIds = (
  conceptBlockIndex: number,
  conceptSubsetIds: ConceptId[],
  searchParams: Record<string, string | string[] | undefined>
) => {
  const singleBlockConcepts = searchParams[`_c`] as string | undefined;
  if (singleBlockConcepts && conceptBlockIndex > 0) {
    throw new SamplingError(
      "Invalid Search Params: Cannot use _c with multiple concept blocks!"
    );
  }
  const urlConceptsString =
    (searchParams[`_c${conceptBlockIndex}`] as string | undefined) ??
    singleBlockConcepts ??
    "";
  const urlConceptIndices = urlConceptsString
    .split(",")
    .filter((c) => c.length > 0)
    .map((c) => parseInt(c));
  if (
    urlConceptIndices.some(
      (c) => isNaN(c) || c < 0 || c >= conceptSubsetIds.length
    )
  ) {
    throw new SamplingError(
      `Invalid Search Params: Invalid indices in _c${conceptBlockIndex}!`
    );
  }
  return urlConceptIndices.map((i) => conceptSubsetIds[i]); // 0-indexed
};

const filterQualifiedConcepts = ({
  concepts,
  qaHistory,
  searchParams,
  additionalData,
}: {
  concepts: Concept[];
  qaHistory: QAState[] | undefined;
  searchParams: Record<string, string | string[] | undefined>;
  additionalData: AdditionalResponseData | undefined;
}): Concept[] => {
  return concepts.filter((concept) => {
    const stringValuedSearchParams = Object.fromEntries(
      Object.entries(searchParams).filter(([, v]) => typeof v === "string")
    ) as Record<string, string>;

    return passesConditional({
      conditional: concept.conditional,
      searchParams: stringValuedSearchParams,
      stateHistory: qaHistory ?? [],
      items: [],
      additionalData,
      conceptId: null,
    });
  });
};

// Time-estimator-only overrides; live interviews never set these.
export type EstimatorConceptSampling = {
  // Replaces the Boolean concept qualification so conditions the estimator
  // cannot simulate do not silently drop a concept.
  isConceptExcluded: (concept: Concept) => boolean;
  // The only block sampled in this call: the one the simulated respondent has
  // reached. Other blocks wait, so no concept is excluded before the answers
  // it depends on have been simulated.
  forceSampleBlockId: InterviewBlockId | undefined;
};

const sampleConceptBlock = ({
  block,
  interviewSampleState,
  conceptBlockIndex,
  onlyTakeConcept,
  includeAllConcepts,
  searchParams,
  qaHistory,
  concepts,
  prevBlocksInterviewItems,
  getFilteredPrevBlocksInterviewItems,
  currTemplateId,
  additionalData,
  screeningBlockId,
  conceptSamplingData,
  randomSeed,
  estimatorConceptSampling,
}: {
  block: InterviewConceptBlock;
  interviewSampleState: InterviewSampleState | undefined;
  conceptBlockIndex: number;
  onlyTakeConcept: "first" | ConceptId | null;
  includeAllConcepts: boolean;
  searchParams: Record<string, string | string[] | undefined>;
  qaHistory: QAState[] | undefined;
  concepts: Concept[];
  prevBlocksInterviewItems: InterviewItem[];
  getFilteredPrevBlocksInterviewItems: () => InterviewItem[];
  currTemplateId: InterviewItemTemplateId | undefined;
  additionalData: AdditionalResponseData | undefined;
  screeningBlockId: InterviewBlockId | undefined;
  conceptSamplingData: AllConceptSamplingData | undefined;
  randomSeed: string | undefined;
  estimatorConceptSampling: EstimatorConceptSampling | undefined;
}): ConceptId[] | null => {
  if (interviewSampleState) {
    const alreadySampledConceptIds =
      interviewSampleState.conceptsByBlock[block.id];
    // already sampled -> don't sample again
    if (alreadySampledConceptIds) {
      const alreadySampledConcepts = alreadySampledConceptIds.map((cId) =>
        concepts.find((c) => c.id === cId)
      );
      if (alreadySampledConcepts.some((c) => !c)) {
        console.error(
          "Sampling specified unknown concept ids",
          alreadySampledConceptIds
        );
      }
      return alreadySampledConcepts.filter(notNull).map((c) => c.id);
    }
  }

  const blockConceptIds =
    block.samplingConfig.onlySubset ?? concepts.map((c) => c.id);
  const blockConcepts = blockConceptIds
    .map((id) => concepts.find((c) => c.id === id))
    .filter(notNull);

  // onlyTakeConcept or includeAllConcepts set -> no sampling required
  if (onlyTakeConcept === "first") {
    return blockConcepts.length === 0 ? [] : [blockConcepts[0].id];
  }
  if (onlyTakeConcept) {
    return blockConcepts.find((c) => c.id === onlyTakeConcept)?.id
      ? [onlyTakeConcept]
      : blockConcepts.length === 0
        ? []
        : [blockConcepts[0].id];
  }
  if (includeAllConcepts) {
    return blockConcepts.map((c) => c.id);
  }

  // waiting for required inputs to become available -> defer sampling
  if (estimatorConceptSampling) {
    if (estimatorConceptSampling.forceSampleBlockId !== block.id) {
      return null;
    }
  } else if (
    !canBlockBeSampled({
      blockConditional: getBlockConditional(block),
      blockConcepts,
      qaHistory,
      onlyTakeConcept,
      includeAllConcepts,
      prevBlocksInterviewItems,
      filteredPrevBlocksInterviewItems: getFilteredPrevBlocksInterviewItems(),
      currTemplateId,
      screeningBlockId,
      additionalData,
    })
  ) {
    return null;
  }

  // Dedicated rng per (response, block): repeated sampling from the same state
  // must agree even before the result lands in interviewSampleState, and must
  // not consume the caller's rng stream (which seeds choice randomization).
  const rng = randomSeed
    ? seedrandom(`${randomSeed}:concept-sampling:${block.id}`)
    : undefined;

  const urlConceptIds = new Set(
    getUrlConceptIds(
      conceptBlockIndex,
      blockConcepts.map((c) => c.id),
      searchParams
    )
  );

  const qualifiedConcepts = estimatorConceptSampling
    ? blockConcepts.filter(
        (concept) => !estimatorConceptSampling.isConceptExcluded(concept)
      )
    : filterQualifiedConcepts({
        concepts: blockConcepts,
        qaHistory,
        searchParams,
        additionalData,
      });
  if (qualifiedConcepts.length === 0) {
    return [];
  }

  const qualifiedUrlConceptIds = new Set(
    qualifiedConcepts.filter((c) => urlConceptIds.has(c.id)).map((c) => c.id)
  );

  // for remaining concepts, apply sampling function
  const qualifiedNonUrlConcepts = qualifiedConcepts.filter(
    (c) => !qualifiedUrlConceptIds.has(c.id)
  );

  const validSamplingParams = safeValidateDynamicSamplingParams({
    functionId:
      block.samplingConfig.conceptSamplingFunctionId ??
      DEFAULT_CONCEPT_SAMPLING_FUNCTION_ID,
    functionArgs: {
      concepts: qualifiedNonUrlConcepts,
      block,
      data: conceptSamplingData,
      rng,
    },
  });
  const sortedConceptIds = sortConceptsForSampling(validSamplingParams);

  // pick the first k concepts (or all if not set)
  const k = block.samplingConfig.subsampleCount;
  const conceptIdsToInclude = [
    ...shuffleArray([...qualifiedUrlConceptIds], rng),
    ...sortedConceptIds,
  ].slice(0, k);

  // debug logs if no concepts are included (typically unexpected)
  if (conceptIdsToInclude.length === 0) {
    console.warn(`no concepts included for block ${block.id} [${block.title}]`);
    console.log("blockConcepts", blockConcepts);
    console.log("qualifiedConcepts", qualifiedConcepts);
    console.log("qualifiedUrlConceptIds", qualifiedUrlConceptIds);
    console.log(
      "qualifiedNonUrlConceptIds",
      qualifiedNonUrlConcepts.map((c) => c.id)
    );
    console.log("sortedConceptIds", sortedConceptIds);
    console.log("k", k);
    return [];
  }

  // show concepts in random order
  return shuffleArray(conceptIdsToInclude, rng);
};

// Author-entered media URLs can carry signing or sharing tokens, in the query or in the path;
// captures keep only where the media lives.
export const toLoggableUrl = (url: string): string => {
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${redactTokenPathSegments(pathname)}`;
  } catch {
    return "[invalid-url]";
  }
};

// Older same-language overlays hold an identical copy of base variants, which is harmless.
const isVariantOverlayLeak = ({
  overlayByLanguage,
  baseByLanguage,
}: {
  overlayByLanguage: unknown;
  baseByLanguage: unknown;
}) =>
  overlayByLanguage !== undefined &&
  JSON.stringify(overlayByLanguage) !== JSON.stringify(baseByLanguage);

// Merge a per-language variant from the corresponding base slot onto the
// overlay slot (or onto the base slot itself when sampling base in a non-null
// language). When no variant exists for `language`, returns `overlaySlot`
// unchanged. Placeholders pass through.
const mergeSlotVariant = (
  overlaySlot: MediaDefOrPlaceholder,
  baseSlot: MediaDefOrPlaceholder | undefined,
  language: AudioLanguage
): MediaDefOrPlaceholder => {
  if (overlaySlot.type === "concept-placeholder") return overlaySlot;
  if (!baseSlot || baseSlot.type === "concept-placeholder") return overlaySlot;
  // Defensive guard: translator strips byLanguage from overlays so variants
  // resolve only against the base. If an overlay ever leaks byLanguage, the
  // sampler would otherwise silently mix stale overlay variants with current
  // base variants. Flag it loudly so a translator regression is detectable.
  if (
    isVariantOverlayLeak({
      overlayByLanguage: overlaySlot.byLanguage,
      baseByLanguage: baseSlot.byLanguage,
    })
  ) {
    logger.captureException(
      new Error("media-variant-overlay-leak"),
      { slotUrl: toLoggableUrl(overlaySlot.url), language },
      areaTag.withValue("sampler")
    );
  }
  const variant = baseSlot.byLanguage?.[language];
  if (!variant) return overlaySlot;
  // Cross-type variant (image base + video variant, or vice versa) is
  // supported: return the variant as-is. Spreading overlay fields would
  // leak video-only keys (show_controls, force_watching) onto an image
  // result and break the discriminated union. The result is a fully-typed
  // MediaDefOrPlaceholder matching the variant's discriminator.
  if (variant.type !== overlaySlot.type) {
    // Watermark and caption are slot-level authoring metadata, not variant
    // content, so they follow the slot even when the variant changes type.
    return {
      ...variant,
      watermark: overlaySlot.watermark,
      caption: overlaySlot.caption,
    };
  }
  const { byLanguage: _byLang, ...rest } = overlaySlot;
  return { ...rest, ...variant };
};

const mergeItemUrlEmbedVariant = (
  overlayEmbed: EmbedDef,
  baseEmbed: EmbedDef,
  language: AudioLanguage
): EmbedDef => {
  if (overlayEmbed.type === "concept_embed") return overlayEmbed;
  if (baseEmbed.type === "concept_embed") return overlayEmbed;
  if (
    isVariantOverlayLeak({
      overlayByLanguage: overlayEmbed.byLanguage,
      baseByLanguage: baseEmbed.byLanguage,
    })
  ) {
    logger.captureException(
      new Error("embed-variant-overlay-leak"),
      { url: toLoggableUrl(overlayEmbed.url), language },
      areaTag.withValue("sampler"),
      variantKindTag.withValue("item")
    );
  }
  const variant = baseEmbed.byLanguage?.[language];
  if (!variant) return overlayEmbed;
  // Assign url + proxyUrl from variant explicitly so a variant without
  // proxyUrl clears any inherited proxy (otherwise getEmbedLoadUrl would
  // still load the base's proxied URL instead of the variant's bare URL).
  const { byLanguage: _byLang, ...rest } = overlayEmbed;
  return { ...rest, url: variant.url, proxyUrl: variant.proxyUrl };
};

const mergeConceptEmbedVariant = (
  overlayEmbed: NonNullable<Concept["content"]["embed"]>,
  baseEmbed: NonNullable<Concept["content"]["embed"]>,
  language: AudioLanguage
): NonNullable<Concept["content"]["embed"]> => {
  if (
    isVariantOverlayLeak({
      overlayByLanguage: overlayEmbed.byLanguage,
      baseByLanguage: baseEmbed.byLanguage,
    })
  ) {
    logger.captureException(
      new Error("embed-variant-overlay-leak"),
      { url: toLoggableUrl(overlayEmbed.url), language },
      areaTag.withValue("sampler"),
      variantKindTag.withValue("concept")
    );
  }
  const variant = baseEmbed.byLanguage?.[language];
  if (!variant) return overlayEmbed;
  const { byLanguage: _byLang, ...rest } = overlayEmbed;
  return { ...rest, url: variant.url, proxyUrl: variant.proxyUrl };
};

/** Base revision + language key for resolving per-slot `byLanguage` media. */
export type BaseDataForMediaVariants = {
  baseData: Pick<
    SurveyDataSingleLanguage,
    "blocks" | "concepts" | "screener_data"
  >;
  language: AudioLanguage;
};

/**
 * Samples a discussion guide
 * Flatten interview blocks and sample concepts to get a list of items
 *
 * @includeAllConcepts - don't subsample or randomize order (but still respect the onlySubset). Used e.g. for aspects
 * @param includeScreenerEndItem - if true, the screener end item will be included in allInterviewItems (but not in interviewItems or screeningInterviewItems)
 * @param searchParams - if url contains parameters like ?_c=0,2 or ?_c0=1&_c1=2,3, use those to sample
 * @param randomizeChoices - if true, the order of the choices will be randomized where enabled
 */
export const sampleSurveyData = (
  data: SurveyData,
  {
    // if null, sample as usual. If "first", take first concept.
    // If conceptId, take that concept (or first for blocks that it is available in)
    onlyTakeConcept = null as null | "first" | ConceptId,
    interviewSampleState = undefined as InterviewSampleState | undefined,
    includeAllConcepts = false,
    includeScreenerEndItem = false,
    includeVideoQualityCheckItem = false,
    searchParams = {} as Record<string, string | string[] | undefined>,
    randomizeChoices = true,
    include_translations = true,
    // Filters which translation overlays are sampled into the result. Media
    // variants use `baseDataForMediaVariants.language`, not this.
    language = undefined as AudioLanguage | undefined,
    // If true, skip resolving carry-forward options - return raw template choices
    skipResolveOptions = false,
    qaHistory = undefined as QAState[] | undefined,
    additionalData = undefined as AdditionalResponseData | undefined,
    // Needed to correctly determine if a concept conditional is waiting for input
    currTemplateId = undefined as InterviewItemTemplateId | undefined,
    // If true, use fast deterministic IDs instead of uuidv5 (for time estimation only)
    fastIds = false,
    conceptSamplingData = undefined as AllConceptSamplingData | undefined,
    randomSeed = undefined as string | undefined,
    // Edit-mode / overlay hook: when `data` is a single-language slice but the
    // caller still wants per-language media overrides applied, pass the live
    // base data and byLanguage key here. The sampler reads each base slot's
    // `byLanguage[language]` while sampling the slice for text.
    baseDataForMediaVariants = undefined as
      | BaseDataForMediaVariants
      | undefined,
    estimatorConceptSampling = undefined as
      | EstimatorConceptSampling
      | undefined,
  } = {}
): SampledSurveyData & {
  interviewSampleState: InterviewSampleState;
} => {
  const rng = randomSeed ? seedrandom(randomSeed) : undefined;
  // Use fast IDs when enabled (avoids expensive uuidv5/sha1 computation)
  const getItemId = fastIds
    ? (templateId: InterviewItemTemplateId, conceptId: ConceptId | null) =>
        `${templateId}:${conceptId ?? "none"}` as InterviewItemId
    : interviewItemIdFromBlock;
  interviewSampleState = interviewSampleState
    ? structuredClone(interviewSampleState)
    : ({ conceptsByBlock: {} } as InterviewSampleState);

  const { blocks, ...rest } = data; // remove blocks from data
  // Some stored revisions have no concepts array.
  const { concepts = [], screener_data } = rest;

  // relace any {{concept.X}} with the concept data
  const replace = (text: string, concept: Concept) => {
    if (!text) return text;
    return substituteConceptPlaceholders(text, {
      title: concept.content.title || null,
      description: concept.content.description || null,
    });
  };

  const screenerRules = screener_data?.screeningCriteria;
  const screeningBlock = blocks.find((b) => b.type === "screening");
  const screeningBlockId = screeningBlock?.id;

  // Per-language overrides live on each base MediaDef as `byLanguage`, and are
  // stripped from translation overlays by the translator. Build lookup maps
  // from base so we can resolve overrides slot-by-slot regardless of which
  // language overlay is currently being sampled. Prefer
  // `baseDataForMediaVariants.baseData` when supplied (preview path: `data` is
  // a translation overlay, the live base lives separately in memory).
  const overrideBaseSource = baseDataForMediaVariants?.baseData;
  const baseItemTemplateById = new Map<
    InterviewItemTemplateId,
    InterviewItemTemplate
  >();
  (overrideBaseSource?.blocks ?? blocks).forEach((b) =>
    b.items.forEach((it) => baseItemTemplateById.set(it.id, it))
  );
  // The video quality check is sampled like a screening item but lives on
  // screener_data, so it would otherwise miss every base lookup below.
  const baseVideoQualityItem = (
    overrideBaseSource?.screener_data ?? screener_data
  )?.videoQualityCheckItem;
  if (baseVideoQualityItem) {
    baseItemTemplateById.set(baseVideoQualityItem.id, baseVideoQualityItem);
  }
  // Keyed by option id across the whole revision rather than per item, so a
  // carry-forward question finds the media on its source question's options.
  const baseOptionMediaById = new Map<OptionId, OptionMedia | undefined>();
  baseItemTemplateById.forEach((it) =>
    it.choices?.forEach((c) => baseOptionMediaById.set(c.id, c.media))
  );
  const baseConceptById = new Map<ConceptId, Concept>(
    (overrideBaseSource?.concepts ?? concepts).map((c) => [c.id, c])
  );

  // Position-based per-slot fallback: for each slot, read
  // base.media[i].byLanguage[language] from the base lookup; if defined, merge
  // variant fields onto the slot (the renderer reads url/type/etc. from the
  // returned slot). The translator preserves slot positions in overlays, so
  // index-based matching is safe.
  const resolveItemMediaForLang = (
    templateId: InterviewItemTemplateId,
    templateMedia: MediaDefOrPlaceholder[] | undefined,
    language: AudioLanguage | null
  ): MediaDefOrPlaceholder[] | undefined => {
    if (!language || !templateMedia) return templateMedia;
    const baseMedia = baseItemTemplateById.get(templateId)?.media;
    if (!baseMedia) {
      // Overlay has media but base lookup missed (templateId not found, or
      // base media stripped). Variants for this slot silently drop to base.
      logger.captureException(
        new Error("media-variant-base-lookup-missed"),
        { templateId, language, overlayLength: templateMedia.length },
        areaTag.withValue("sampler"),
        variantKindTag.withValue("item")
      );
      return templateMedia;
    }
    if (baseMedia.length !== templateMedia.length) {
      logger.captureException(
        new Error("media-variant-length-mismatch"),
        {
          templateId,
          language,
          baseLength: baseMedia.length,
          overlayLength: templateMedia.length,
        },
        areaTag.withValue("sampler"),
        variantKindTag.withValue("item")
      );
    }
    return templateMedia.map(
      (m, i): MediaDefOrPlaceholder =>
        mergeSlotVariant(m, baseMedia[i], language)
    );
  };

  const resolveConceptForLang = (
    concept: Concept,
    language: AudioLanguage | null
  ): Concept => {
    if (!language) return concept;
    const baseConcept = baseConceptById.get(concept.id);
    const baseMedia = baseConcept?.content.media;
    const overlayMedia = concept.content.media;
    const baseEmbed = baseConcept?.content.embed;
    const overlayEmbed = concept.content.embed;

    // Resolve media (per-slot byLanguage, lives on each base MediaDef).
    let resolvedMedia: MediaDef[] | undefined = overlayMedia;
    if (overlayMedia) {
      if (!baseMedia) {
        logger.captureException(
          new Error("media-variant-base-lookup-missed"),
          {
            conceptId: concept.id,
            language,
            overlayLength: overlayMedia.length,
          },
          areaTag.withValue("sampler"),
          variantKindTag.withValue("concept")
        );
      } else {
        if (baseMedia.length !== overlayMedia.length) {
          logger.captureException(
            new Error("media-variant-length-mismatch"),
            {
              conceptId: concept.id,
              language,
              baseLength: baseMedia.length,
              overlayLength: overlayMedia.length,
            },
            areaTag.withValue("sampler"),
            variantKindTag.withValue("concept")
          );
        }
        resolvedMedia = overlayMedia.map((m, i) => {
          const merged = mergeSlotVariant(m, baseMedia[i], language);
          return merged.type === "concept-placeholder" ? m : merged;
        });
      }
    }

    const resolvedEmbed =
      overlayEmbed && baseEmbed
        ? mergeConceptEmbedVariant(overlayEmbed, baseEmbed, language)
        : overlayEmbed;
    if (overlayEmbed && !baseEmbed) {
      logger.captureException(
        new Error("embed-variant-base-lookup-missed"),
        { conceptId: concept.id, language },
        areaTag.withValue("sampler"),
        variantKindTag.withValue("concept")
      );
    }

    if (resolvedMedia === overlayMedia && resolvedEmbed === overlayEmbed) {
      return concept;
    }
    return {
      ...concept,
      content: {
        ...concept.content,
        ...(resolvedMedia !== overlayMedia ? { media: resolvedMedia } : {}),
        ...(resolvedEmbed !== overlayEmbed ? { embed: resolvedEmbed } : {}),
      },
    };
  };

  const resolveItemEmbedForLang = (
    templateId: InterviewItemTemplateId,
    overlayEmbed: EmbedDef | undefined,
    language: AudioLanguage | null
  ): EmbedDef | undefined => {
    if (!language || !overlayEmbed) return overlayEmbed;
    if (overlayEmbed.type === "concept_embed") return overlayEmbed;
    const baseEmbed = baseItemTemplateById.get(templateId)?.embed;
    if (!baseEmbed || baseEmbed.type === "concept_embed") {
      logger.captureException(
        new Error("embed-variant-base-lookup-missed"),
        { templateId, language },
        areaTag.withValue("sampler"),
        variantKindTag.withValue("item")
      );
      return overlayEmbed;
    }
    return mergeItemUrlEmbedVariant(overlayEmbed, baseEmbed, language);
  };

  // A translation overlay's option media is a snapshot that is never refreshed,
  // and option media is language independent, so the base data wins by id.
  const withBaseOptionMedia = (
    choices: Option[],
    language: AudioLanguage | null
  ): Option[] =>
    // Keyed on the sampled language, not on `overrideBaseSource`: overlays are
    // also sampled from inside a full revision, where base is `blocks`.
    !language
      ? choices
      : choices.map((choice) =>
          // `has`, not a truthy check, so an option the base does not know
          // about keeps whatever it arrived with.
          baseOptionMediaById.has(choice.id)
            ? { ...choice, media: baseOptionMediaById.get(choice.id) }
            : choice
        );

  const resolveAndRandomize = (
    item: Omit<InterviewItemTemplate, "media">,
    templates: Omit<InterviewItemTemplate, "media">[],
    language: AudioLanguage | null
  ) => {
    const choices = withBaseOptionMedia(
      skipResolveOptions
        ? (getOptionsUnsafe(item) ?? [])
        : OptionsResolverUtils.getOptions(item, templates, screenerRules),
      language
    );

    if (choices.length === 0) return [];
    // Only randomize for multiple_choice or ranking questions
    if (!isMultipleChoice(item) && !isRanking(item)) {
      return choices;
    }
    if (item.randomize_choices && randomizeChoices) {
      const pinned = choices.filter((c) => c.pinned || c.exclusiveOption);
      const unpinned = choices.filter((c) => !c.pinned && !c.exclusiveOption);
      const shuffled = shuffleArray(unpinned, rng);
      return [...shuffled, ...pinned];
    }
    return choices;
  };

  // Matrix randomization shuffles rows (the items being evaluated). Columns
  // are the shared answer scale and stay in their authored order.
  const resolveAndRandomizeMatrixRows = (
    item: Omit<InterviewItemTemplate, "media">
  ) => {
    if (!isMatrix(item)) return undefined;
    const rows = item.rows ?? [];
    if (rows.length === 0) return rows;
    if (item.randomize_choices && randomizeChoices) {
      return shuffleArray(rows, rng);
    }
    return rows;
  };
  const blocksToInterviewItems = (
    blocks: InterviewBlock[],
    {
      translatedConcepts,
      screener_data,
      language,
      templates,
    }: {
      translatedConcepts?: Concept[];
      screener_data?: ScreenerData;
      language: AudioLanguage | null;
      templates: Omit<InterviewItemTemplate, "media">[];
    }
  ): InterviewItem[] => {
    const prevBlocksInterviewItems: InterviewItem[] = [];
    let conceptBlockIndex = 0;

    const getFilteredPrevBlocksInterviewItems = () => {
      const normalizedSearchParams = Object.entries(searchParams).reduce<
        Record<string, string>
      >((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = Array.isArray(value) ? value[0] : value;
        }
        return acc;
      }, {});

      return filterInterviewParts({
        items: prevBlocksInterviewItems,
        searchParams: normalizedSearchParams,
        stateHistory: qaHistory ?? [],
        additionalData,
        // filteredInterviewItems is only used to determine whether we are ready to sample concept
        // blocks or not. In edit mode, we always just sample the first concept so we don't need to handle it here.
        isEditMode: false,
      });
    };

    blocks.forEach((block) => {
      switch (block.type) {
        case "screening":
        case "flat": {
          const items = block.items.map(({ id, media, embed, ...template }) => {
            const resolvedEmbed = resolveItemEmbedForLang(id, embed, language);
            const directEmbed =
              resolvedEmbed?.type === "concept_embed"
                ? undefined
                : resolvedEmbed
                  ? {
                      ...resolvedEmbed,
                      type: resolvedEmbed.type ?? "url_embed",
                    }
                  : undefined;
            const matrixRows = resolveAndRandomizeMatrixRows({
              ...template,
              id,
            });
            const resolvedMedia = resolveItemMediaForLang(id, media, language);
            const common = {
              id: getItemId(id, null),
              blockId: block.id,
              templateId: id,
              conceptId: null as ConceptId | null,
              media: getMediaDefs({ media: resolvedMedia }),
              embed: directEmbed,
              choices: resolveAndRandomize(
                { ...template, id },
                templates,
                language
              ),
              conditional: combineConditionals(
                getBlockConditional(block),
                template.conditional
              ),
            };
            return template.type === "question"
              ? {
                  ...template,
                  ...common,
                  addInstructions: getActiveAddInstructions(template),
                  ...(matrixRows !== undefined ? { rows: matrixRows } : {}),
                }
              : { ...template, ...common };
          });
          if (
            block.type === "screening" &&
            screener_data &&
            screener_data.videoQualityCheckItem &&
            includeVideoQualityCheckItem
          ) {
            const template = screener_data.videoQualityCheckItem;
            const exist_screening_criteria =
              screener_data.screeningCriteria.some(
                (criteria) => criteria.interview_question_item === template.id
              );
            if (exist_screening_criteria) {
              const interviewItem = {
                ...template,
                id: getItemId(template.id, null),
                blockId: block.id,
                templateId: template.id,
                conceptId: null as ConceptId | null,
                media: [],
                embed: undefined,
                choices: resolveAndRandomize(template, templates, language),
                conditional: combineConditionals(
                  getBlockConditional(block),
                  template.conditional
                ),
              };
              items.push(interviewItem);
            } else {
              console.error(
                "screening criteria not found for video quality item"
              );
            }
          }
          prevBlocksInterviewItems.push(...items);
          break;
        }
        case "concept": {
          if (!translatedConcepts) {
            throw new Error("invalid arguments");
          }

          const conceptsToInterviewItems = (sampledConceptIds: ConceptId[]) => {
            const conceptById = new Map(
              translatedConcepts.map((c) => [
                c.id,
                resolveConceptForLang(c, language),
              ])
            );
            return sampledConceptIds
              .map((cId) => conceptById.get(cId)!)
              .flatMap((c) =>
                block.items.map(
                  ({ id, media, embed, ...template }): InterviewItem => {
                    const task =
                      template.type === "question" ? template.task : undefined;
                    const resolvedItemEmbed = resolveItemEmbedForLang(
                      id,
                      embed,
                      language
                    );
                    const usesConceptEmbed =
                      resolvedItemEmbed?.type === "concept_embed";
                    const urlEmbed =
                      resolvedItemEmbed &&
                      resolvedItemEmbed.type !== "concept_embed"
                        ? resolvedItemEmbed
                        : undefined;
                    const templateEmbedUrl = urlEmbed?.url
                      ? replace(urlEmbed.url, c)
                      : undefined;
                    const conceptEmbedUrl =
                      usesConceptEmbed && c.content.embed?.url
                        ? replace(c.content.embed.url, c)
                        : undefined;
                    const finalEmbedUrl = templateEmbedUrl ?? conceptEmbedUrl;
                    const finalProxyUrl = usesConceptEmbed
                      ? c.content.embed?.proxyUrl
                      : urlEmbed?.proxyUrl;
                    const finalEmbedWatermark = usesConceptEmbed
                      ? c.content.embed?.watermark
                      : urlEmbed?.watermark;
                    const matrixRows = resolveAndRandomizeMatrixRows({
                      ...template,
                      id,
                    })?.map((r) => ({ ...r, text: replace(r.text, c) }));
                    const resolvedMedia = resolveItemMediaForLang(
                      id,
                      media,
                      language
                    );

                    const common = {
                      id: getItemId(id, c.id),
                      blockId: block.id,
                      templateId: id,
                      conceptId: c.id,
                      media: task
                        ? []
                        : getMediaDefs({
                            media: resolvedMedia,
                            currentConcept: c,
                          }),
                      text: replace(template.text, c),
                      embed:
                        !task && finalEmbedUrl
                          ? {
                              type: "url_embed" as const,
                              url: finalEmbedUrl,
                              proxyUrl: finalProxyUrl,
                              watermark: finalEmbedWatermark,
                            }
                          : undefined,
                      choices: resolveAndRandomize(
                        { ...template, id },
                        templates,
                        language
                      )?.map((o) => ({
                        ...o,
                        text: replace(o.text, c),
                      })),
                      conditional: combineConditionals(
                        getBlockConditional(block),
                        // concept conditionals can hide all items of this concept
                        // TODO(soon): this would ideally be on the sampling level
                        // right now it only really works if you sample all concepts
                        // In the editor, we only sample one so don't apply the logic there
                        onlyTakeConcept !== null ? undefined : c.conditional,
                        template.conditional
                      ),
                    };
                    const activeAddInstructions =
                      template.type === "question"
                        ? getActiveAddInstructions(template)
                        : undefined;
                    const interviewItem: InterviewItem =
                      template.type === "question"
                        ? {
                            ...template,
                            ...common,
                            ...(task
                              ? {
                                  isTaskUrlFromConcept:
                                    task.destinationSource !== "previous" &&
                                    task.destinationSource !== "custom" &&
                                    !task.url?.trim() &&
                                    !!c.content.embed?.url,
                                  task: {
                                    ...task,
                                    url:
                                      task.url?.trim() ||
                                      (task.destinationSource !== "previous" &&
                                      task.destinationSource !== "custom" &&
                                      c.content.embed?.url
                                        ? replace(c.content.embed.url, c)
                                        : undefined),
                                  },
                                }
                              : {}),
                            addInstructions: activeAddInstructions
                              ? replace(activeAddInstructions, c)
                              : undefined,
                            ...(matrixRows !== undefined
                              ? { rows: matrixRows }
                              : {}),
                          }
                        : { ...template, ...common };
                    return interviewItem;
                  }
                )
              );
          };

          const sampledConceptIds = sampleConceptBlock({
            block,
            interviewSampleState,
            conceptBlockIndex,
            onlyTakeConcept,
            includeAllConcepts,
            searchParams,
            qaHistory,
            concepts,
            prevBlocksInterviewItems,
            getFilteredPrevBlocksInterviewItems,
            currTemplateId,
            additionalData,
            screeningBlockId,
            conceptSamplingData,
            randomSeed,
            estimatorConceptSampling,
          });

          conceptBlockIndex++;

          if (!sampledConceptIds) {
            // can't realize interview items yet - add a placeholder until concepts are sampled
            const syntheticTemplateId =
              `unsampled_${block.id}` as InterviewItemTemplateId;
            // generate example items for estimating the weight of the realized items on the progress bar
            const blockConceptIds =
              block.samplingConfig.onlySubset ?? concepts.map((c) => c.id);
            const exampleInterviewItems = conceptsToInterviewItems(
              blockConceptIds.slice(
                0,
                // assume this is how many concepts will be sampled (may change if concepts are disqualified
                // based on conditionals)
                block.samplingConfig.subsampleCount
              )
            );
            prevBlocksInterviewItems.push({
              type: "unsampled_concept_block",
              id: getItemId(syntheticTemplateId, null),
              blockId: block.id,
              templateId: syntheticTemplateId,
              conceptId: null,
              text: "",
              media: [],
              embed: undefined,
              choices: undefined,
              progressWeight: exampleInterviewItems.reduce(
                (acc, item) => acc + questionWeight(item),
                0
              ),
            } satisfies InterviewItem);
            break;
          }

          // add to interviewSampleState to keep sampled concepts consistent across languages
          interviewSampleState.conceptsByBlock[block.id] = sampledConceptIds;

          prevBlocksInterviewItems.push(
            ...conceptsToInterviewItems(sampledConceptIds)
          );
        }
      }
    });
    // we've processed every block so this now contains interview items from all blocks
    return prevBlocksInterviewItems;
  };

  const buildMaxDiffDesignsForItems = ({
    items,
    existingDesigns,
    seedBase,
  }: {
    items: InterviewItem[];
    existingDesigns: Record<InterviewItemId, MaxDiffDesign>;
    seedBase: string | undefined;
  }): Record<InterviewItemId, MaxDiffDesign> => {
    const maxDiffDesignsByItem = { ...existingDesigns };
    items.filter(isMaxDiff).forEach((item) => {
      maxDiffDesignsByItem[item.id] ??= buildMaxDiffDesign(item, seedBase);
    });
    return maxDiffDesignsByItem;
  };

  const buildConjointDesignsForItems = ({
    items,
    existingDesigns,
    seedBase,
  }: {
    items: InterviewItem[];
    existingDesigns: Record<InterviewItemId, ConjointDesign>;
    seedBase: string | undefined;
  }): Record<InterviewItemId, ConjointDesign> => {
    const conjointDesignsByItem = { ...existingDesigns };
    items.filter(isConjoint).forEach((item) => {
      conjointDesignsByItem[item.id] ??= buildConjointDesign(item, seedBase);
    });
    return conjointDesignsByItem;
  };

  const buildGaborGrangerDesignsForItems = ({
    items,
    existingDesigns,
    seedBase,
  }: {
    items: InterviewItem[];
    existingDesigns: Record<InterviewItemId, GaborGrangerDesign>;
    seedBase: string | undefined;
  }): Record<InterviewItemId, GaborGrangerDesign> => {
    const gaborGrangerDesignsByItem = { ...existingDesigns };
    items.filter(isGaborGranger).forEach((item) => {
      gaborGrangerDesignsByItem[item.id] ??= buildGaborGrangerDesign(
        item,
        seedBase
      );
    });
    return gaborGrangerDesignsByItem;
  };

  const getItemsSingleLanguage = (
    langData: SurveyDataSingleLanguage,
    language: AudioLanguage | null
  ) => {
    const langTemplates = langData.blocks.flatMap((b) => b.items);
    const blockInterviewItems = blocksToInterviewItems(langData.blocks, {
      translatedConcepts: langData.concepts,
      screener_data: langData.screener_data,
      language,
      templates: langTemplates,
    });
    const interviewItems = blockInterviewItems.filter((item) => {
      const block = langData.blocks.find((b) => b.id === item.blockId);
      return block?.type !== "screening";
    });
    const screeningInterviewItems = blockInterviewItems.filter((item) => {
      const block = langData.blocks.find((b) => b.id === item.blockId);
      return block?.type === "screening";
    });

    const endTemplate = langData.screener_data?.screeningEndItem;
    const blockId =
      langData.blocks.find((b) => b.type === "screening")?.id ??
      (uuidv4() as InterviewBlockId);
    const endItem: InterviewItem | undefined =
      endTemplate && includeScreenerEndItem
        ? {
            ...endTemplate,
            id: getItemId(endTemplate.id, null),
            blockId,
            templateId: endTemplate.id,
            conceptId: null,
            media: [],
          }
        : undefined;
    const allInterviewItems: InterviewItem[] = [
      ...screeningInterviewItems,
      endItem,
      ...interviewItems,
    ].filter(notNull);

    return {
      interviewItems,
      screeningInterviewItems,
      allInterviewItems,
    };
  };

  const allTranslationEntries = Object.entries(data.translations ?? {});
  const translationEntries = language
    ? allTranslationEntries.filter(([lang]) => lang === language)
    : include_translations
      ? allTranslationEntries
      : [];
  const fullTranslations = Object.fromEntries(
    translationEntries.map(([lang, langData]) => [
      lang,
      {
        ...langData,
        ...getItemsSingleLanguage(langData, lang as AudioLanguage),
      },
    ])
  );

  const baseLanguageItems = getItemsSingleLanguage(
    data,
    baseDataForMediaVariants?.language ?? null
  );

  interviewSampleState.maxDiffDesignsByItem = buildMaxDiffDesignsForItems({
    items: [
      ...baseLanguageItems.allInterviewItems,
      ...Object.values(fullTranslations).flatMap(
        (translation) => translation.allInterviewItems
      ),
    ],
    existingDesigns: interviewSampleState.maxDiffDesignsByItem ?? {},
    seedBase: randomSeed,
  });

  interviewSampleState.conjointDesignsByItem = buildConjointDesignsForItems({
    items: [
      ...baseLanguageItems.allInterviewItems,
      ...Object.values(fullTranslations).flatMap(
        (translation) => translation.allInterviewItems
      ),
    ],
    existingDesigns: interviewSampleState.conjointDesignsByItem ?? {},
    seedBase: randomSeed,
  });
  interviewSampleState.gaborGrangerDesignsByItem =
    buildGaborGrangerDesignsForItems({
      items: [
        ...baseLanguageItems.allInterviewItems,
        ...Object.values(fullTranslations).flatMap(
          (translation) => translation.allInterviewItems
        ),
      ],
      existingDesigns: interviewSampleState.gaborGrangerDesignsByItem ?? {},
      seedBase: randomSeed,
    });

  const result = {
    ...rest,
    ...baseLanguageItems,
    translations: fullTranslations,
    screener_data,
    interviewSampleState,
  };

  return result;
};

// samples survey data for a responses; always in the analysis language
export const sampleSurveyDataForResponse = (
  surveyRevision: ImportantRevisionData,
  responseData: FreeResponse,
  options: {
    includeScreenerEndItem?: boolean;
    includeVideoQualityCheckItem?: boolean;
    disableRandomizeChoices?: boolean;
  } = {}
): SampledSurveyDataSingleLanguage => {
  const config = get_config(surveyRevision.config);
  const analysisLanguage = config.analysis_language;
  const overlay = surveyRevision.data.translations?.[analysisLanguage];
  const relevantData = overlay ?? surveyRevision.data;
  // Per-slot byLanguage variants live on each base MediaDef and are stripped
  // from translation overlays by the translator. When sampling an overlay
  // slice for analysis, hand the base data in separately so variants resolve.
  // Skip the hint when the analysis language equals the base language (no
  // override resolution needed) or when no overlay is loaded for this
  // language. The no-overlay branch falls through to base text and therefore
  // intentionally also skips variant resolution: mixing variant assets
  // localized for `analysisLanguage` on top of base-language text would be
  // worse than uniform base output.
  const baseDataForMediaVariants =
    overlay && analysisLanguage !== config.question_language
      ? { baseData: surveyRevision.data, language: analysisLanguage }
      : undefined;
  const sampledData = sampleSurveyData(relevantData, {
    interviewSampleState: responseData.interviewSampleState,
    includeScreenerEndItem: options.includeScreenerEndItem ?? true,
    includeVideoQualityCheckItem: options.includeVideoQualityCheckItem ?? true,
    randomizeChoices: !options.disableRandomizeChoices,
    additionalData: responseData.additionalData,
    baseDataForMediaVariants,
  });
  return sampledData;
};

export const interviewItemIdFromBlock = (
  item: InterviewItemTemplateId,
  conceptId: ConceptId | null
) => {
  if (conceptId) {
    return uuidv5(item + conceptId, uuidv5.URL) as InterviewItemId;
  } else {
    return uuidv5(item, uuidv5.URL) as InterviewItemId;
  }
};

export const formatRawAssistantMsg = (
  msg: string,
  index: number,
  additionalData?: string
) =>
  `${msg}\n####\n${index}${additionalData ? `\n####\n${additionalData}` : ""}`;

// Stands in for a skipped turn's blank answer. Scoped to "this question" so the
// moderator does not read it as leave to skip later questions.
export const SKIPPED_TURN_MARKER =
  "[The participant skipped this question. Do not ask it again or follow up on it.]";

export const rawMessagesFromHistory = (
  history: QAState[],
  interviewItems: InterviewItem[]
) => {
  const itemById = new Map(
    interviewItems.map((item, index) => [item.id, { ...item, index }])
  );
  return history
    .filter((state) => itemById.get(state.currentItemId)) // for edit mode
    .flatMap((state) => [
      {
        role: "assistant" as const,
        content: formatRawAssistantMsg(
          state.assMsg,
          itemById.get(state.currentItemId)!.index,
          state.additionalData
        ),
      },
      {
        role: "user" as const,
        content: state.skipped ? SKIPPED_TURN_MARKER : state.rawInput,
      },
    ])
    .filter(notNull);
};

/** Return the 3 ids used for state history */
export const interviewItemIds = (item: InterviewItem) => {
  return {
    currentItemId: item.id,
    currentTemplateId: item.templateId,
    currentConceptId: item.conceptId,
  };
};
